import React, { useEffect, useState, useRef } from 'react';
import Header from '../page/Header';
const JavaCompiler = () => {
  // Define the initial Java code to be displayed in the editable editor
  const initialJavaCode = `public class MyProgram {
    public static void main(String[] args) {
        // Your Java code goes here
        System.out.println("Hello from the Java Compiler!");
        // Try changing this message and running the code!
        // Example error: System.out.println("Missing semicolon"
    }
}`;

  // State variables for the component
  const [displayedCode, setDisplayedCode] = useState(initialJavaCode); // Code currently in the editor (now editable)
  const [consoleOutput, setConsoleOutput] = useState(''); // Output displayed in the console
  const [isCompiling, setIsCompiling] = useState(false); // Shows "Compiling..." message
  const [lineNumbers, setLineNumbers] = useState([]); // State to hold line numbers
  const [errorLine, setErrorLine] = useState(null); // State to hold the line number where an error occurred

  // Refs for scroll synchronization between textarea and line numbers
  const textAreaRef = useRef(null);
  const lineNumbersRef = useRef(null);

  // Effect to update line numbers whenever displayedCode changes
  useEffect(() => {
    // Split the code by newlines to count lines
    const lines = displayedCode.split('\n');
    // Generate an array of numbers from 1 to the number of lines
    const numbers = Array.from({ length: lines.length }, (_, i) => i + 1);
    setLineNumbers(numbers);
  }, [displayedCode]); // Dependency array: re-run when displayedCode changes

  // Function to handle changes in the code editor (textarea)
  const handleCodeChange = (event) => {
    setDisplayedCode(event.target.value);
    setErrorLine(null); // Clear error line when code is changed
  };

  // Function to synchronize scrolling between textarea and line numbers
  const handleScroll = () => {
    if (textAreaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textAreaRef.current.scrollTop;
    }
  };

  // Function to handle the "Run" button click
  const handleRun = async () => { // Made the function async to use await
    setIsCompiling(true); // Show compiling message
    setConsoleOutput(''); // Clear previous output immediately
    setErrorLine(null); // Clear any previous error line

    try {
      // Send the code data to the specified API endpoint
      const response = await fetch('http://localhost:8090/api/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain', // Changed content type to text/plain
        },
        // Send the displayedCode directly as the request body
        body: displayedCode,
      });

      // Check if the HTTP response was successful
      if (!response.ok) {
        const errorText = await response.text(); // Read error message from response body
        let detectedErrorLine = null;

        // Attempt to parse line number from common compiler error formats
        // Pattern 1: <filename>:<line_number>: (e.g., <stdin>:11:)
        const pattern1Match = errorText.match(/<stdin>:(\d+):/);
        if (pattern1Match && pattern1Match[1]) {
          detectedErrorLine = parseInt(pattern1Match[1], 10);
        }

        // Pattern 2: "line <line_number>" (e.g., Error on line 11, or at line 11)
        if (detectedErrorLine === null) { // Only try this if pattern 1 didn't find anything
            const pattern2Match = errorText.match(/[Ll]ine\s+(\d+)/);
            if (pattern2Match && pattern2Match[1]) {
                detectedErrorLine = parseInt(pattern2Match[1], 10);
            }
        }

        // Set the error line if found
        if (detectedErrorLine !== null) {
          setErrorLine(detectedErrorLine);
        }
        throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorText}`);
      }

      // Get the output from the API response as plain text
      const output = await response.text();
      setConsoleOutput(output); // Display the API's output in the console
    } catch (error) {
      // Handle any errors that occur during the fetch operation
      console.error("Error during code execution:", error);
      setConsoleOutput(`Error: Could not connect to the compiler service or an error occurred during execution.
Please ensure the backend server is running at http://localhost:8090.
Details: ${error.message || 'Unknown error'}`);
    } finally {
      setIsCompiling(false); // Hide compiling message regardless of success or failure
    }
  };

  // Function to handle the "Clear" button click
  const handleClear = () => {
    setConsoleOutput(''); // Clear the console output
    setErrorLine(null); // Clear error line on clear
  };

  // Inline styles for the component
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e, #16213e, #0f3460)', // Deep space gradient
      color: '#e0e0e0', // Light grey text
      fontFamily: '"Inter", sans-serif', // Modern font
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'hidden', // Prevent scrollbars from overflow
      position: 'relative',
    },
    title: {
      fontSize: 'clamp(2rem, 5vw, 3.5rem)',
      fontWeight: 'bold',
      marginBottom: '30px',
      background: 'linear-gradient(90deg, #00c6ff, #0072ff)', // Blue gradient for title
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textShadow: '0 0 15px rgba(0, 198, 255, 0.6)', // Subtle glow
      textAlign: 'center',
    },
    compilerArea: {
      display: 'flex',
      flexDirection: 'column',
      width: 'clamp(300px, 90vw, 800px)',
      background: 'rgba(25, 25, 40, 0.8)', // Darker, slightly transparent background
      backdropFilter: 'blur(10px)', // Glassmorphism effect
      borderRadius: '20px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.6), inset 0 0 30px rgba(0,0,0,0.4)',
      border: '1px solid rgba(0, 198, 255, 0.2)', // Light blue border
      padding: '25px',
      marginBottom: '30px',
    },
    // New style for the container holding line numbers and textarea
    codeEditorContainer: {
      display: 'flex', // Use flexbox to put line numbers and textarea side-by-side
      minHeight: '250px',
      maxHeight: '400px',
      background: '#0a0a1a', // Very dark background for code editor area
      border: '1px solid #0072ff', // Blue border
      borderRadius: '10px',
      boxShadow: 'inset 0 0 10px rgba(0,255,198,0.3)', // Inner glow
      marginBottom: '20px',
      resize: 'vertical', // Allow vertical resizing of the entire editor area
      overflow: 'hidden', // Hide scrollbars on the container itself
    },
    lineNumbers: {
      width: '40px', // Fixed width for line numbers
      padding: '15px 5px 15px 10px', // Padding for line numbers
      textAlign: 'right',
      color: '#666', // Grey color for line numbers
      background: '#050510', // Slightly darker background for line number column
      borderRight: '1px solid #0072ff', // Separator line
      fontSize: 'clamp(0.8rem, 1.2vw, 1.0rem)',
      fontFamily: 'monospace',
      flexShrink: 0, // Prevent shrinking
      overflowY: 'hidden', // Hide scrollbar on line numbers, controlled by textarea
      userSelect: 'none', // Prevent selection of line numbers
    },
    lineNumberItem: { // Style for individual line number div
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        height: '1.2em', // Match line height of textarea
        lineHeight: '1.2em', // Ensure consistent line height
    },
    codeTextArea: {
      flexGrow: 1, // Allow textarea to take remaining space
      background: 'transparent', // Make textarea background transparent to show container background
      border: 'none', // Remove textarea's default border
      borderRadius: '0 10px 10px 0', // Round only right corners
      padding: '15px', // Padding for the text content
      fontSize: 'clamp(0.8rem, 1.2vw, 1.0rem)',
      color: '#00ffc6', // Greenish-blue for code
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      outline: 'none', // Remove default outline on focus
      resize: 'none', // Disable textarea's own resize handle
      overflowY: 'auto', // Enable scrolling for textarea
      lineHeight: '1.2em', // Explicitly set line height for textarea
    },
    buttonsContainer: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    button: {
      padding: '12px 25px',
      fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)',
      fontWeight: 'bold',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
      outline: 'none',
    },
    runButton: {
      background: 'linear-gradient(45deg, #28a745, #218838)', // Green gradient
      color: '#fff',
      // No direct pseudo-class support in inline styles, but for visual intent:
      // '&:hover': { background: 'linear-gradient(45deg, #218838, #1e7e34)', transform: 'translateY(-2px)', boxShadow: '0 7px 20px rgba(0,255,0,0.4)' },
      // '&:active': { transform: 'translateY(0)', boxShadow: '0 3px 10px rgba(0,0,0,0.3)' },
      // '&:disabled': { opacity: 0.6, cursor: 'not-allowed', boxShadow: 'none' }
    },
    clearButton: {
      background: 'linear-gradient(45deg, #dc3545, #c82333)', // Red gradient
      color: '#fff',
      // No direct pseudo-class support in inline styles, but for visual intent:
      // '&:hover': { background: 'linear-gradient(45deg, #c82333, #bd2130)', transform: 'translateY(-2px)', boxShadow: '0 7px 20px rgba(255,0,0,0.4)' },
      // '&:active': { transform: 'translateY(0)', boxShadow: '0 3px 10px rgba(0,0,0,0.3)' },
      // '&:disabled': { opacity: 0.6, cursor: 'not-allowed', boxShadow: 'none' }
    },
    consoleArea: {
      minHeight: '120px',
      maxHeight: '250px',
      overflowY: 'auto',
      background: '#0a0a1a', // Very dark background for console
      border: '1px solid #ffaa00', // Orange border
      borderRadius: '10px',
      padding: '15px',
      fontSize: 'clamp(0.8rem, 1.2vw, 1.0rem)',
      color: '#ffaa00', // Orange for console output
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      boxShadow: 'inset 0 0 10px rgba(255,170,0,0.3)', // Inner glow
      position: 'relative',
    },
    compilingMessage: {
      color: '#00c6ff', // Blue for compiling message
      textAlign: 'center',
      fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)',
      fontWeight: 'bold',
      animation: 'pulse 1.5s infinite alternate', // Pulsing animation
    },
    errorIcon: {
        marginLeft: '5px',
        color: 'red', // Red color for the error icon
        fontSize: '1em', // Adjust size as needed
        lineHeight: '1', // Ensure it doesn't affect line height
    },
    // Keyframes for animations (defined as string to be injected into style tag)
    keyframes: `
      @keyframes pulse {
        0% { opacity: 0.6; }
        100% { opacity: 1; }
      }
    `,
  };

  // Inject keyframes into the document head using a useEffect
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles.keyframes;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [styles.keyframes]); // Re-run if keyframes string changes (unlikely here)

  return (
    <>
    <Header/>
    <br/>
    <div style={styles.container}>
      <h1 style={styles.title}>Java Code Playground</h1>

      <div style={styles.compilerArea}>
        <div style={styles.codeEditorContainer}>
          <div ref={lineNumbersRef} style={styles.lineNumbers}>
            {lineNumbers.map((num) => (
              <div key={num} style={styles.lineNumberItem}>
                {num}
                {errorLine === num && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="red"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={styles.errorIcon}
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                )}
              </div>
            ))}
          </div>
          <textarea
            ref={textAreaRef}
            style={styles.codeTextArea}
            value={displayedCode}
            onChange={handleCodeChange}
            onScroll={handleScroll} // Attach scroll handler
            spellCheck="false" // Disable spell check for code
            autoCorrect="off" // Disable auto-correct
            autoCapitalize="off" // Disable auto-capitalize
            wrap="off" // Prevent automatic wrapping (though pre-wrap handles visual wrapping)
          />
        </div>

        <div style={styles.buttonsContainer}>
          <button
            style={{ ...styles.button, ...styles.runButton }}
            onClick={handleRun}
            disabled={isCompiling} // Disable run while compiling
          >
            Run Code
          </button>
          <button
            style={{ ...styles.button, ...styles.clearButton }}
            onClick={handleClear}
            disabled={isCompiling} // Disable clear while compiling
          >
            Clear Console
          </button>
        </div>

        <div style={styles.consoleArea}>
          {isCompiling ? (
            <div style={styles.compilingMessage}>Compiling...</div>
          ) : (
            consoleOutput
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default JavaCompiler;
